// The plain-English area summary. Pure (no DOM), so it's unit-tested in test/questions.test.ts.
// The guided questions themselves live in metrics.yaml (served by /api/metrics).

import type { Scores } from "./types.ts";
import { forecastConfidence, type TrackRecord } from "./context.ts";
import { outlookOf } from "./outlook.ts";
import { MONTHS, pctChange } from "./format.ts";

type Latest = Record<string, { period: string; value: number } | undefined>;
export type Tone = "buyer" | "seller" | "neutral";

export interface AreaSummary {
  /** "Buyer's market" / "Seller's market" / "Balanced market", or null when fewer than 2 market signals exist;
   *  leverage is the Buyer Leverage Score (0-100) when the area has one */
  market: { label: string; tone: Tone; signals: number; leverage: number | null } | null;
  points: Array<{ topic: string; text: string; tone: Tone }>;
}

const monthsBetween = (a: string, b: string) => {
  const [ay, am] = a.split("-").map(Number) as [number, number];
  const [by, bm] = b.split("-").map(Number) as [number, number];
  return (by - ay) * 12 + (bm - am);
};
const pctAbs = (v: number, d = 0) => `${Math.abs(v * 100).toFixed(d)}%`;
const pct = (v: number, d = 0) => `${(v * 100).toFixed(d)}%`;
const usd = (v: number) => `$${Math.round(Math.abs(v)).toLocaleString("en-US")}`;
const usdK = (v: number) => (Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1000)}k`);

/**
 * Plain-English read of an area from its latest values, compared with the U.S. where that helps.
 * `us` is the national latest values (pass null for the U.S. itself). Rules are fixed thresholds, written to be
 * explainable rather than clever; every sentence names the number it's based on.
 */
export function summarize(area: Latest, us: Latest | null, forecast?: Scores["forecast"] | null, track?: TrackRecord | null): AreaSummary {
  const v = (id: string) => area[id]?.value;
  const u = (id: string) => (us ? us[id]?.value : undefined);
  const has = (x: number | undefined): x is number => x != null && Number.isFinite(x);
  // sources update on different schedules (Redfin can lag months): date any number older than the area's newest
  const newest = Object.values(area).reduce((a, x) => (x && x.period > a ? x.period : a), "");
  const asOf = (id: string) => {
    const p = area[id]?.period;
    if (!p || !newest || monthsBetween(p, newest) < 2) return "";
    const [y, m] = p.split("-");
    return ` (as of ${MONTHS[Number(m) - 1]} ${y})`;
  };
  const points: AreaSummary["points"] = [];

  // --- market balance: each signal votes +1 (buyers), -1 (sellers) or 0
  const votes: number[] = [];
  const vote = (x: number | undefined, buyerIf: (x: number) => boolean, sellerIf: (x: number) => boolean) => {
    if (has(x)) votes.push(buyerIf(x) ? 1 : sellerIf(x) ? -1 : 0);
  };
  const rel = (id: string) => (has(v(id)) && has(u(id)) && u(id)! > 0 ? v(id)! / u(id)! : undefined);
  vote(v("inventory_vs_2019"), (x) => x >= 0, (x) => x <= -0.25);
  vote(v("sale_to_list"), (x) => x < 0.985, (x) => x >= 1);
  vote(v("months_supply"), (x) => x > 6, (x) => x < 4);
  vote(v("inventory_yoy"), (x) => x > 0.1, (x) => x < -0.1);
  vote(rel("price_cut_pct"), (x) => x > 1.15, (x) => x < 0.85);
  vote(rel("dom"), (x) => x > 1.15, (x) => x < 0.85);
  let market: AreaSummary["market"] = null;
  const lev = v("buyer_leverage");
  if (votes.length >= 2) {
    const avg = votes.reduce((a, b) => a + b, 0) / votes.length;
    const leverage = has(lev) ? Math.round(lev) : null;
    market = avg >= 0.34 ? { label: "Buyer's market", tone: "buyer", signals: votes.length, leverage }
      : avg <= -0.34 ? { label: "Seller's market", tone: "seller", signals: votes.length, leverage }
      : { label: "Balanced market", tone: "neutral", signals: votes.length, leverage };
  }

  // --- valuation
  const ov = v("overvalued_pct");
  if (has(ov)) {
    if (ov >= 0.1) points.push({ topic: "Valuation", tone: "seller",
      text: `Prices are ${pctAbs(ov)} above what local incomes have historically supported (this area's pre-2020 price-to-income ratio).` });
    else if (ov <= -0.05) points.push({ topic: "Valuation", tone: "buyer",
      text: `Prices are ${pctAbs(ov)} below this area's pre-2020 price-to-income norm, cheaper relative to incomes than before the pandemic.` });
    else points.push({ topic: "Valuation", tone: "neutral",
      text: `Prices are roughly in line with local incomes (${pctChange(ov, 0)} vs. this area's pre-2020 price-to-income norm).` });
  }

  // --- price trend
  const yoy = v("home_value_yoy");
  if (has(yoy)) {
    const peak = v("pct_from_2022_peak");
    const dir = yoy >= 0.005 ? `up ${pctAbs(yoy, 1)}` : yoy <= -0.005 ? `down ${pctAbs(yoy, 1)}` : "flat";
    const peakTxt = has(peak) && peak <= -0.03 ? `${yoy > 0 ? ", but still" : ","} ${pctAbs(peak)} below their 2022-23 peak` : "";
    const usTxt = has(u("home_value_yoy")) ? ` (U.S.: ${pctChange(u("home_value_yoy")!)})` : "";
    points.push({ topic: "Prices", tone: yoy < 0 ? "buyer" : yoy > 0.05 ? "seller" : "neutral",
      text: `Home values are ${dir} over the past year${usTxt}${peakTxt}.` });
  }

  // --- supply
  const inv = v("inventory_vs_2019");
  if (has(inv)) {
    const iy = v("inventory_yoy");
    const trend = has(iy) && Math.abs(iy) >= 0.05 ? ` and ${iy > 0 ? "rising" : "falling"} (${pctChange(iy, 0)} vs. a year ago)` : "";
    points.push(inv >= 0
      ? { topic: "Supply", tone: "buyer", text: `There are ${pctAbs(inv)} more homes for sale than the 2017-19 norm${trend}, so buyers have more to choose from than before the pandemic.` }
      : inv <= -0.25
        ? { topic: "Supply", tone: "seller", text: `Listings are ${pctAbs(inv)} below the 2017-19 norm${trend}: still a tight market.` }
        : { topic: "Supply", tone: "neutral", text: `Listings are ${pctAbs(inv)} below the 2017-19 norm${trend}, ${inv > -0.1 ? "close to pre-pandemic levels" : "still somewhat tight"}.` });
  }

  // --- negotiation
  const stl = v("sale_to_list");
  const cut = v("price_cut_pct");
  if (has(stl) || has(cut) || has(lev)) {
    const parts: string[] = [];
    if (has(stl)) parts.push((stl >= 1
      ? `homes typically sell at or above asking (${pct(stl, 1)} of list)`
      : `homes sell for about ${pctAbs(1 - stl, 1)} below their final list price`) + asOf("sale_to_list"));
    if (has(cut)) parts.push(`${pct(cut)} of listings had a price cut${has(u("price_cut_pct")) ? ` (U.S.: ${pct(u("price_cut_pct")!)})` : ""}${asOf("price_cut_pct")}`);
    const s = parts.join("; ");
    // the score's cut-offs: in Sep 2026 over 90% of the ZIPs the vote above called a buyer's market scored 65+, and seller's 35 or less
    const levTone: Tone | null = has(lev) ? (lev >= 65 ? "buyer" : lev <= 35 ? "seller" : "neutral") : null;
    const levTxt = has(lev)
      ? `Buyer leverage is ${levTone === "buyer" ? "high" : levTone === "seller" ? "low" : "moderate"} (${Math.round(lev)}/100${has(u("buyer_leverage")) ? `; U.S.: ${Math.round(u("buyer_leverage")!)}` : ""})${asOf("buyer_leverage")}`
      : "";
    const text = levTxt && s ? `${levTxt}: ${s}.` : levTxt ? `${levTxt}.` : `${s[0]!.toUpperCase()}${s.slice(1)}.`;
    points.push({ topic: "Negotiating", tone: levTone ?? (has(stl) && stl >= 1 ? "seller" : has(stl) && stl < 0.985 ? "buyer" : "neutral"), text });
  }

  // --- affordability
  const ppi = v("payment_pct_income");
  if (has(ppi)) {
    const sal = v("salary_to_afford");
    const usTxt = has(u("payment_pct_income")) ? ` (U.S.: ${pct(u("payment_pct_income")!)})` : "";
    const salTxt = has(sal) ? `; it takes about ${usdK(sal)} a year to keep the payment at 30% of gross pay` : "";
    const ins = v("insurance_premium");
    const insTxt = has(ins) ? ` That includes about ${usd(ins)} a year for home insurance${has(u("insurance_premium")) ? ` (U.S.: ${usd(u("insurance_premium")!)})` : ""}.` : "";
    points.push({ topic: "Affordability", tone: ppi > 0.4 ? "seller" : ppi < 0.25 ? "buyer" : "neutral",
      text: `A mortgage on the typical home takes ${pct(ppi)} of the median household income${usTxt}${salTxt}.${insTxt}` });
  }

  // --- buy vs. rent
  const bvr = v("buy_vs_rent");
  if (has(bvr)) points.push(Math.abs(bvr) < 100
    ? { topic: "Buy vs. rent", tone: "neutral", text: "Owning and renting a typical home cost about the same per month." }
    : { topic: "Buy vs. rent", tone: bvr > 0 ? "seller" : "buyer",
        text: `Buying costs about ${usd(bvr)}/mo ${bvr > 0 ? "more" : "less"} than renting a typical home (mortgage payment vs. typical rent).` });

  // --- natural-disaster risk (FEMA National Risk Index): dollars of expected yearly damage per $100k of buildings
  const hz = v("hazard_loss");
  if (has(hz) && hz > 0) {
    const parts: Array<[string, string]> = [["hurricane", "hurricanes"], ["flood", "flooding"], ["wildfire", "wildfire"], ["quake", "earthquakes"], ["storm", "tornadoes, hail and wind"]];
    const [topId, topName] = parts.map(([id, name]) => [v(`hazard_loss_${id}`) ?? 0, id, name] as const).sort((a, b) => b[0] - a[0])
      .filter(([x]) => x >= 0.4 * hz).map(([, id, name]) => [id, name] as const)[0] ?? [];
    const usHz = u("hazard_loss");
    const high = has(usHz) && hz >= 1.5 * usHz;
    const cover = topId === "flood" ? " Flood isn't covered by standard homeowners insurance." : topId === "quake" ? " Earthquakes aren't covered by standard homeowners insurance." : "";
    points.push({ topic: "Disaster risk", tone: "neutral",
      text: `FEMA expects about $${Math.round(hz)} a year in natural-disaster damage per $100k of building value${has(usHz) ? ` (U.S.: $${Math.round(usHz)})` : ""}${topName ? `, mostly from ${topName}` : ""}.${high ? cover || " Check insurance costs before buying." : ""}` });
  }

  // --- outlook
  if (forecast?.label && forecast.likely) {
    const { lo, hi, p_rise } = forecast.likely;
    // where the area's own record trails the naive guess (shared/context.ts), the odds are the model's and it says so
    const c = track === undefined ? null : forecastConfidence(track);
    const low = c?.kind === "low" ? c : null;
    const chance = p_rise == null ? "" : low ? ` (the model's chance of a rise: ${Math.round(p_rise * 100)}%)` : ` (${Math.round(p_rise * 100)}% chance of a rise)`;
    points.push({ topic: "Outlook", tone: outlookOf(forecast.label) === "down" ? "buyer" : outlookOf(forecast.label) === "up" ? "seller" : "neutral",
      text: `${forecast.label}: likely ${pctChange(lo, 0)} to ${pctChange(hi, 0)} over the next 12 months${chance}.` +
        (low ? ` Low confidence: here, assuming last year repeats has called the direction ${Math.round(low.naive * 100)}% of the time, the model ${Math.round(low.model * 100)}%.` : "") });
  }

  return { market, points };
}

/** The summary points shown before "N more": these topics answer "should I buy here?" first; any gap is filled in the
 *  summary's own order. The rest keep their order. */
const LEAD_TOPICS = ["Valuation", "Prices", "Affordability"];
export function splitSummary<P extends { topic: string }>(points: P[], n = 3): { lead: P[]; more: P[] } {
  const lead = LEAD_TOPICS.map((t) => points.find((p) => p.topic === t)).filter((p): p is P => !!p);
  for (const p of points) if (lead.length < n && !lead.includes(p)) lead.push(p);
  // shown in the summary's own order, so the lead reads the same way as the full list
  const shown = points.filter((p) => lead.slice(0, n).includes(p));
  return { lead: shown, more: points.filter((p) => !shown.includes(p)) };
}
